/* ============================================================================
   CostControls — admin "Cost controls" tab (ISS-25)
   Loaded as its own screen file (mirrors kbadmin.jsx): main.jsx imports it BEFORE
   admin.jsx, it assigns window.CostControls, and admin.jsx mounts <CostControls/>.

   Makes the swarm per-run cost ceiling an admin-editable, per-workspace setting
   (was the hardcoded $5 in swarm/lib/jobstate.py::DEFAULT_COST_CEILING_USD).
   Also surfaces the guardrails hangman flagged in ISS-25: a soft-warn threshold,
   a monthly workspace budget, and an optional per-stage cap.

   PERSISTENCE: loads from / saves to the backend via window.GooseAPI
   (getCostControls / saveCostControls). Falls back to localStorage so it still
   renders if the endpoint isn't wired yet.

   Dependencies (all global in this codebase): React, I.* icons, window.MAV,
   window.GooseAPI. CSS: the .cost-* block appended to styles/app.css.
   ============================================================================ */

const { useState: useCostState, useEffect: useCostEffect } = React;

const CEIL_PRESETS = [2.5, 5, 10, 25];           // $ per build
const DEFAULT_CEILING = 5;                        // == swarm DEFAULT_COST_CEILING_USD
const STAGE_PRESETS = [0.5, 1, 2, 3];             // $ per stage (advanced)

const COST_DEFAULTS = {
  ceiling: 5,            // hard abort cap, $/build  → SWARM_COST_CEILING_USD
  softWarnOn: true,
  softWarnPct: 70,       // notify (don't abort) at this % of the ceiling
  monthlyBudget: 3000,   // $/workspace/month
  perStageOn: false,
  perStageCap: 2,        // $/stage when enabled
};

function loadCostCfgLocal() {
  try {
    const raw = localStorage.getItem('goose-cost-controls');
    if (raw) return { ...COST_DEFAULTS, ...JSON.parse(raw) };
  } catch (e) {}
  return { ...COST_DEFAULTS };
}

function fmtUSD(n) {
  return '$' + (Number(n) % 1 === 0 ? Number(n).toLocaleString() : Number(n).toFixed(2));
}

function CostControls() {
  const MAV = window.MAV;
  const [cfg, setCfg] = useCostState(loadCostCfgLocal);
  const [saved, setSaved] = useCostState(loadCostCfgLocal);   // last-persisted snapshot
  const [toast, setToast] = useCostState(false);
  const [busy, setBusy] = useCostState(false);
  const [customCeil, setCustomCeil] = useCostState(!CEIL_PRESETS.includes(loadCostCfgLocal().ceiling));

  // hydrate from the backend on mount (falls back to whatever localStorage had)
  useCostEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (window.GooseAPI && window.GooseAPI.getCostControls) {
          const server = await window.GooseAPI.getCostControls();
          if (alive && server) {
            const merged = { ...COST_DEFAULTS, ...server };
            setCfg(merged); setSaved(merged);
            setCustomCeil(!CEIL_PRESETS.includes(merged.ceiling));
          }
        }
      } catch (e) { /* keep localStorage fallback */ }
    })();
    return () => { alive = false; };
  }, []);

  const set = (patch) => setCfg(c => ({ ...c, ...patch }));
  const dirty = JSON.stringify(cfg) !== JSON.stringify(saved);

  const save = async () => {
    setBusy(true);
    // Attempt the server save in ITS OWN try so a failure (route not deployed yet /
    // network) can't abort the local persist + the user feedback below. The old code
    // awaited the API first inside one try → a 404/500 fell straight to a silent
    // console.error: no toast, no persist, no dirty-clear → "Save does nothing".
    let serverOk = false;
    try {
      if (window.GooseAPI && window.GooseAPI.saveCostControls) {
        await window.GooseAPI.saveCostControls(cfg);   // throws on non-2xx (ISS req())
        serverOk = true;
      }
    } catch (e) {
      console.warn('cost-controls server save failed (kept local):', e && e.message);
    }
    // ALWAYS persist locally so the choice survives a reload (the panel hydrates from
    // localStorage on mount; the server value, when present, overrides it). NOTE: a
    // local-only save does NOT reach the swarm — only the server value → spawn env
    // does — hence the honest "backend not deployed yet" toast when serverOk is false.
    try { localStorage.setItem('goose-cost-controls', JSON.stringify(cfg)); } catch (e) {}
    setSaved(cfg);
    setToast(serverOk ? 'ok' : 'local');
    setTimeout(() => setToast(false), serverOk ? 2400 : 4200);
    setBusy(false);
  };
  const reset = () => setCfg(saved);

  const warnAmount = (cfg.ceiling * cfg.softWarnPct / 100);
  const monthSpend = (MAV.team || []).reduce((s, m) => s + (m.used || 0), 0);
  const budgetPct = cfg.monthlyBudget > 0 ? Math.min(100, Math.round(monthSpend / cfg.monthlyBudget * 100)) : 0;

  return (
    <div className="fade-in cost-controls">
      <div className="cost-grid">
        {/* ── VD-03 · Default build engine (upstream of the $ controls) ── */}
        {window.WorkspaceEngineControl ? <WorkspaceEngineControl /> : null}
        {/* ── Per-build ceiling (the hero) ── */}
        <div className="card card-pad">
          <div className="sec-head">
            <span className="sec-title"><I.bolt size={15} style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--fail)' }} />Per-build cost ceiling</span>
            {cfg.ceiling === DEFAULT_CEILING && <span className="chip" style={{ fontSize: 10.5 }}>Default</span>}
          </div>
          <p className="cost-help">A build that crosses this is <b>aborted mid-run</b> (between stages, and before each paid retry) so a wedged run can never spend unbounded. Enforced per build across the whole squadron.</p>

          <div className="cost-bignum">{fmtUSD(cfg.ceiling)}<span className="cost-unit">/ build</span></div>

          <div className="row gap-8 wrap" style={{ marginTop: 4 }}>
            {CEIL_PRESETS.map(v => (
              <button key={v} type="button" className={'bc-chip' + (!customCeil && cfg.ceiling === v ? ' on' : '')}
                onClick={() => { setCustomCeil(false); set({ ceiling: v }); }}>{fmtUSD(v)}</button>
            ))}
            <button type="button" className={'bc-chip' + (customCeil ? ' on' : '')} onClick={() => setCustomCeil(true)}>Custom</button>
          </div>

          {customCeil && (
            <div className="cost-custom">
              <span className="cost-dollar">$</span>
              <input className="input" type="number" min="0.5" step="0.5" value={cfg.ceiling}
                onChange={e => set({ ceiling: Math.max(0.5, Number(e.target.value) || 0) })} style={{ maxWidth: 130 }} />
              <span className="muted" style={{ fontSize: 12 }}>per build</span>
            </div>
          )}
        </div>

        {/* ── Soft-warn threshold ── */}
        <div className="card card-pad">
          <div className="sec-head">
            <span className="sec-title"><I.warn size={15} style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--cond)' }} />Soft-warn threshold</span>
            <button type="button" className={'switch' + (cfg.softWarnOn ? ' on' : '')} onClick={() => set({ softWarnOn: !cfg.softWarnOn })} aria-label="Toggle soft warn"><span className="knob"></span></button>
          </div>
          <p className="cost-help">Notify the build owner &amp; admins when a run crosses this share of the ceiling — <b>without aborting</b>. An early heads-up before the hard cap bites.</p>

          <div className={'cost-slider-wrap' + (cfg.softWarnOn ? '' : ' disabled')}>
            <div className="row between" style={{ marginBottom: 8 }}>
              <span className="mono" style={{ fontSize: 13, fontWeight: 700 }}>{cfg.softWarnPct}% of ceiling</span>
              <span className="muted mono" style={{ fontSize: 12 }}>≈ {fmtUSD(warnAmount)}</span>
            </div>
            <input className="cost-range" type="range" min="40" max="95" step="5" value={cfg.softWarnPct}
              disabled={!cfg.softWarnOn} onChange={e => set({ softWarnPct: Number(e.target.value) })} />
            <div className="cost-range-ticks"><span>40%</span><span>95%</span></div>
          </div>
        </div>

        {/* ── Monthly workspace budget ── */}
        <div className="card card-pad">
          <div className="sec-head">
            <span className="sec-title"><I.usage size={15} style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--running)' }} />Monthly workspace budget</span>
            <span className="muted" style={{ fontSize: 11.5 }}>Surfaced on Usage &amp; budgets</span>
          </div>
          <p className="cost-help">The soft monthly cap across <b>all</b> builds in this workspace. Drives the budget meters on the Dashboard and Usage screens.</p>

          <div className="cost-custom" style={{ marginTop: 2 }}>
            <span className="cost-dollar">$</span>
            <input className="input" type="number" min="0" step="100" value={cfg.monthlyBudget}
              onChange={e => set({ monthlyBudget: Math.max(0, Number(e.target.value) || 0) })} style={{ maxWidth: 150 }} />
            <span className="muted" style={{ fontSize: 12 }}>/ month</span>
          </div>

          <div className="cost-budget-bar" title={fmtUSD(monthSpend) + ' spent this month'}>
            <div className="cost-budget-fill" style={{ width: budgetPct + '%', background: budgetPct > 85 ? 'var(--fail)' : budgetPct > 60 ? 'var(--cond)' : 'var(--pass)' }}></div>
          </div>
          <div className="row between" style={{ marginTop: 6 }}>
            <span className="muted mono" style={{ fontSize: 11.5 }}>{fmtUSD(monthSpend)} used</span>
            <span className="muted mono" style={{ fontSize: 11.5 }}>{budgetPct}% of budget</span>
          </div>
        </div>

        {/* ── Per-stage cap (advanced) ── */}
        <div className="card card-pad">
          <div className="sec-head">
            <span className="sec-title"><I.layers size={15} style={{ verticalAlign: '-2px', marginRight: 6, color: 'var(--muted)' }} />Per-stage cap <span className="chip" style={{ fontSize: 9.5, marginLeft: 4 }}>Advanced</span></span>
            <button type="button" className={'switch' + (cfg.perStageOn ? ' on' : '')} onClick={() => set({ perStageOn: !cfg.perStageOn })} aria-label="Toggle per-stage cap"><span className="knob"></span></button>
          </div>
          <p className="cost-help">Optionally cap any <i>single</i> stage (Ingestion → Approval). Catches one runaway stage even when the whole-build ceiling has headroom. Off by default.</p>

          <div className={'row gap-8 wrap' + (cfg.perStageOn ? '' : ' cost-dim')} style={{ marginTop: 4 }}>
            {STAGE_PRESETS.map(v => (
              <button key={v} type="button" className={'bc-chip' + (cfg.perStageCap === v ? ' on' : '')}
                disabled={!cfg.perStageOn} onClick={() => set({ perStageCap: v })}>{fmtUSD(v)}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Save bar ── */}
      <div className="cost-savebar">
        <div className="muted" style={{ fontSize: 12 }}>
          {dirty ? <span style={{ color: 'var(--cond)', fontWeight: 600 }}>Unsaved changes</span> : 'All changes saved'}
          <span className="cost-savebar-sep">·</span>
          Applies to <b style={{ color: 'var(--ink-2)' }}>new builds</b> across the workspace.
        </div>
        <div className="row gap-10">
          {dirty && <button className="btn btn-ghost" onClick={reset} disabled={busy}>Discard</button>}
          <button className="btn btn-primary" onClick={save} disabled={!dirty || busy}><I.check size={15} /> {busy ? 'Saving…' : 'Save controls'}</button>
        </div>
      </div>

      <p className="cost-backend"><I.shield size={12} style={{ verticalAlign: '-2px' }} /> Per-workspace &amp; RLS-scoped. The ceiling reaches each swarm run at spawn (<span className="mono">workspaces.cost_controls.ceiling → SWARM_COST_CEILING_USD</span>); <span className="mono">{fmtUSD(DEFAULT_CEILING)}</span> stays the fallback when unset.</p>

      {toast && (
        <div className="cost-toast" style={toast === 'local' ? { background: 'var(--cond)' } : null}>
          <I.check size={15} /> {toast === 'local' ? 'Saved locally — backend not deployed yet' : 'Cost controls saved'}
        </div>
      )}
    </div>
  );
}

// Mirror kbadmin.jsx: expose on window so admin.jsx (imported AFTER this file in
// main.jsx) can mount <CostControls/> as a bare global reference.
window.CostControls = CostControls;
